/**
 * List Table Row Component
 *
 * Displays a single workspace item (table, linked table, or folder) as a row in the list view.
 *
 * Features:
 * - Single click: select item + show preview (after 250ms delay)
 * - Double click: navigate to item detail view
 * - Shift+click: range selection
 * - Cmd/Ctrl+click: toggle selection
 * - Draft table handling: resume creation on any click
 * - Drag and drop support for moving items
 * - Context menu for item actions
 * - Visual feedback for selection and drag state
 *
 * @module features/workspace/components/FolderView/ListTableRow
 */

import { useCallback, useEffect, useRef, forwardRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Star } from 'lucide-react';
import { cn, ContextMenu, ContextMenuTrigger } from '@loader/ui';
import type { WorkspaceItemType } from '@loader/shared-types';

import type { WorkspaceItem } from './types';
import { formatDate, getKindLabel, formatBytes } from './utils';
import { ItemContextMenu } from '../ItemContextMenu';
import { useSelectionStore } from '../../stores/useSelectionStore';
import { useSidePanelStore } from '../../stores/useSidePanelStore';
import { useRenameLogic } from '../../hooks/useRenameLogic';
import { CLICK_DELAY_MS } from '../../constants';
import { ConnectorLabel } from '../ConnectorLabel';

import { WorkspaceIcon } from '@/components/icons';

// ============================================================================
// TYPES
// ============================================================================

interface ListTableRowProps {
  item: WorkspaceItem;
  /** All item IDs in the current view (required for range selection) */
  allItemIds: string[];
  isTrash?: boolean;
  /** Whether user can edit (editor/owner) - viewers cannot modify items */
  canEdit?: boolean;
  /** Whether this item is selected */
  isSelected?: boolean;

  // Action callbacks
  // Note: onRename is handled internally via useRenameLogic hook
  onDuplicate?: (id: string) => void;
  onToggleFavorite?: (id: string, type: WorkspaceItemType) => void;
  onMove?: (id: string, type: WorkspaceItemType) => void;
  onDelete?: (id: string, type: WorkspaceItemType, name?: string) => void;
  onRestore?: (id: string, type: WorkspaceItemType) => void;
  onDeletePermanently?: (id: string, type: WorkspaceItemType) => void;
  onPreview?: (id: string, type: WorkspaceItemType) => void;
  onResumeDraft?: (id: string) => void;

  // Drag and drop handlers
  onDragStart?: (e: React.DragEvent, id: string, type: WorkspaceItemType) => void;
  onDragEnd?: () => void;
  onDragOver?: (e: React.DragEvent, id: string) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent, id: string) => void;
  isDragging?: boolean;
  isDropTarget?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const ListTableRow = forwardRef<HTMLTableRowElement, ListTableRowProps>(
  (
    {
      item,
      allItemIds,
      isTrash,
      canEdit = true,
      isSelected = false,
      onDuplicate,
      onToggleFavorite,
      onMove,
      onDelete,
      onRestore,
      onDeletePermanently,
      onPreview,
      onResumeDraft,
      onDragStart,
      onDragEnd,
      onDragOver,
      onDragLeave,
      onDrop,
      isDragging,
      isDropTarget,
    },
    ref
  ) => {
    const navigate = useNavigate();
    const clickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // ============================================================================
    // RENAME LOGIC
    // ============================================================================

    // Rename functionality - handles inline editing, disabled for viewers
    const {
      inputRef: renameInputRef,
      isRenaming,
      renameValue,
      setRenameValue,
      handleStartRename,
      handleSaveRename,
      handleKeyDown: handleRenameKeyDown,
    } = useRenameLogic(item.id, item.type, item.name, canEdit);

    // ============================================================================
    // STATE AND STORES
    // ============================================================================

    // Selection store - single source of truth for selection state
    const { selectItem, toggleSelect, selectRange, selectedIds } = useSelectionStore();

    // Calculate selected count for multi-select context menu
    // Only apply multi-select behavior if this item is selected
    const selectedCount = isSelected ? selectedIds.size : 1;

    // ============================================================================
    // LIFECYCLE
    // ============================================================================

    // Cleanup click timeout on unmount to prevent memory leaks
    useEffect(() => {
      return () => {
        if (clickTimeoutRef.current) {
          clearTimeout(clickTimeoutRef.current);
          clickTimeoutRef.current = null;
        }
      };
    }, []);

    // ============================================================================
    // SELECTION HANDLING
    // ============================================================================

    /**
     * Handle item selection with keyboard modifiers
     * - Shift+click: range selection from last selected item
     * - Cmd/Ctrl+click: toggle selection (multi-select)
     * - Normal click: single selection
     */
    const handleSelect = useCallback(
      (e: React.MouseEvent) => {
        if (e.shiftKey) {
          // Shift+click: select range from last selected to current
          selectRange(item.id, allItemIds);
        } else if (e.metaKey || e.ctrlKey) {
          // Cmd/Ctrl+click: toggle selection for multi-select
          toggleSelect(item.id);
        } else {
          // Normal click: clear previous selection and select only this item
          selectItem(item.id);
        }
      },
      [item.id, allItemIds, selectItem, toggleSelect, selectRange]
    );

    // ============================================================================
    // NAVIGATION
    // ============================================================================

    /**
     * Get navigation props for folder items
     * Tables and linked tables use the overlay pattern instead
     */
    const getFolderLinkProps = useCallback(() => {
      return { to: '/workspace/folder/$id' as const, params: { id: item.id } };
    }, [item.id]);

    // ============================================================================
    // CLICK HANDLING
    // ============================================================================

    // Side panel store for double-click expand
    const { openPreview, expand } = useSidePanelStore();

    /**
     * Handle row click with distinction between single and double click:
     *
     * DRAFT TABLES:
     * - Any click: immediately select and resume draft creation
     *
     * NORMAL ITEMS:
     * - Single click: select immediately, show preview after 250ms delay
     * - Double click: cancel single-click timeout and:
     *   - For folders: navigate to folder
     *   - For tables/linked tables: expand preview overlay
     *
     * The delay allows us to distinguish between single and double clicks
     * while still providing immediate visual feedback via selection.
     */
    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        // Don't handle clicks while renaming
        if (isRenaming) return;

        e.preventDefault();
        e.stopPropagation();

        // SPECIAL CASE: Draft tables
        // Resume creation immediately on any click
        if (item.type === 'table' && item.status === 'draft' && onResumeDraft) {
          clearClickTimeout();
          handleSelect(e);
          onResumeDraft(item.id);
          return;
        }

        // DOUBLE CLICK DETECTION
        // If timeout is active, this is the second click
        if (clickTimeoutRef.current) {
          clearClickTimeout();

          // Folders: navigate to folder view (trash-aware)
          if (item.type === 'folder') {
            if (isTrash) {
              void navigate({ to: '/workspace/trash', search: { folder: item.id } });
            } else {
              void navigate(getFolderLinkProps());
            }
            return;
          }

          // Tables/Linked Tables: expand preview overlay
          openPreview({
            id: item.id,
            type: item.type,
            name: item.name,
            description: item.description,
            isFavorite: item.isFavorite,
            updatedAt: item.updatedAt,
            createdAt: item.createdAt,
            connectorType: item.connectorType,
          });
          expand();
          return;
        }

        // SINGLE CLICK - Part 1: Immediate selection
        handleSelect(e);

        // SINGLE CLICK - Part 2: Delayed preview
        // Only open preview on normal click (no modifiers)
        // Multi-select (Cmd/Ctrl+click or Shift+click) should not trigger preview
        if (!e.metaKey && !e.ctrlKey && !e.shiftKey) {
          // Set timeout to show preview after delay (if not double-clicked)
          clickTimeoutRef.current = setTimeout(() => {
            clickTimeoutRef.current = null;
            onPreview?.(item.id, item.type);
          }, CLICK_DELAY_MS);
        }
      },
      [item, navigate, getFolderLinkProps, handleSelect, onPreview, onResumeDraft, openPreview, expand, isRenaming]
    );

    // ============================================================================
    // DRAG AND DROP HANDLING
    // ============================================================================

    /**
     * Handle drag start event
     * All item types (tables, linked tables, folders) can be dragged
     * Disabled for viewers
     */
    const handleDragStart = useCallback(
      (e: React.DragEvent) => {
        if (!canEdit) return;
        onDragStart?.(e, item.id, item.type);
      },
      [item.id, item.type, onDragStart, canEdit]
    );

    /**
     * Handle drag over event for folders (drop targets)
     * Disabled for viewers
     */
    const handleDragOver = useCallback(
      (e: React.DragEvent) => {
        if (!canEdit) return;
        if (item.type === 'folder') {
          onDragOver?.(e, item.id);
        }
      },
      [item.id, item.type, onDragOver, canEdit]
    );

    /**
     * Handle drag leave event for folders
     */
    const handleDragLeave = useCallback(
      (e: React.DragEvent) => {
        if (item.type === 'folder') {
          onDragLeave?.(e);
        }
      },
      [item.type, onDragLeave]
    );

    /**
     * Handle drop event for folders
     * Disabled for viewers
     */
    const handleDrop = useCallback(
      (e: React.DragEvent) => {
        if (!canEdit) return;
        if (item.type === 'folder') {
          onDrop?.(e, item.id);
        }
      },
      [item.id, item.type, onDrop, canEdit]
    );

    // ============================================================================
    // HELPER FUNCTIONS
    // ============================================================================

    /**
     * Clear any pending click timeout
     */
    const clearClickTimeout = (): void => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
        clickTimeoutRef.current = null;
      }
    };

    /**
     * Get size/count display for the item
     * Only tables show storage size, other types show "—"
     */
    const getSizeDisplay = (): string => {
      if (item.type === 'table' && item.storageBytes) {
        return formatBytes(item.storageBytes);
      }
      return '—';
    };

    /**
     * Get row CSS classes based on state
     */
    const getRowClassName = (): string => {
      const classes = [
        'group',
        'cursor-pointer',
        'border-b',
        'border-border/50',
        'transition-colors',
      ];

      // Selection state
      if (isSelected) {
        classes.push('bg-primary', 'text-primary-foreground');
      } else {
        classes.push('hover:bg-muted/50');
      }

      // Drag state - make item semi-transparent
      if (isDragging) {
        classes.push('opacity-40');
      }

      // Drop target state - highlight as valid drop zone
      if (isDropTarget) {
        classes.push('bg-accent', 'ring-2', 'ring-inset', 'ring-primary');
      }

      return classes.join(' ');
    };

    /**
     * Get text color class based on selection state
     */
    const getTextColorClass = (): string => {
      return isSelected ? 'text-primary-foreground/70' : 'text-muted-foreground';
    };

    // ============================================================================
    // RENDER
    // ============================================================================

    return (
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <tr
            ref={ref}
            data-workspace-item
            className={getRowClassName()}
            onClick={handleClick}
            draggable={!isRenaming && canEdit}
            onDragStart={handleDragStart}
            onDragEnd={onDragEnd}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {/* Name column with icon and favorite star */}
            <td className="px-3 py-2">
              <div className="flex items-center gap-2">
                <div className="relative shrink-0">
                  <WorkspaceIcon type={item.type} size="sm" />
                </div>
                {isRenaming ? (
                  <input
                    ref={renameInputRef}
                    type="text"
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={handleRenameKeyDown}
                    onBlur={handleSaveRename}
                    onClick={(e) => e.stopPropagation()}
                    className="flex-1 min-w-0 rounded bg-transparent px-1 text-sm font-medium outline-none ring-1 ring-primary"
                  />
                ) : (
                  <span className="truncate font-medium text-sm">{item.name}</span>
                )}
                {!isRenaming && item.connectorType && (
                  <ConnectorLabel connectorType={item.connectorType} isSelected={isSelected} />
                )}
                {!isRenaming && item.isFavorite && (
                  <Star className="size-3 fill-amber-400 text-amber-400 shrink-0" />
                )}
              </div>
            </td>

            {/* Status column (only for tables and linked tables) */}
            <td className={cn('w-20 px-3 py-2 text-sm truncate hidden sm:table-cell', getTextColorClass())}>
              {(item.type === 'table' || item.type === 'linked_table') && item.status ? (
                <span className="flex items-center gap-1.5">
                  <span
                    className={`size-2 rounded-full ${
                      item.status === 'active'
                        ? 'bg-green-500'
                        : item.status === 'draft'
                          ? 'bg-amber-500'
                          : 'bg-gray-400'
                    }`}
                  />
                  <span className="capitalize">{item.status}</span>
                </span>
              ) : (
                '—'
              )}
            </td>

            {/* Kind column */}
            <td className={cn('w-28 px-3 py-2 text-sm truncate', getTextColorClass())}>
              {getKindLabel(item.type)}
            </td>

            {/* Size column (hidden on smaller screens) */}
            <td className={cn('w-24 px-3 py-2 text-sm truncate hidden md:table-cell', getTextColorClass())}>
              {getSizeDisplay()}
            </td>

            {/* Date Created column (hidden on smaller screens) */}
            <td className={cn('w-40 px-3 py-2 text-sm truncate hidden lg:table-cell', getTextColorClass())}>
              {formatDate(item.createdAt)}
            </td>

            {/* Date Modified column */}
            <td className={cn('w-40 px-3 py-2 text-sm truncate', getTextColorClass())}>
              {formatDate(item.updatedAt)}
            </td>
          </tr>
        </ContextMenuTrigger>

        {/* Context menu for item actions */}
        <ItemContextMenu
          id={item.id}
          type={item.type}
          name={item.name}
          isFavorite={item.isFavorite ?? false}
          isTrash={isTrash}
          selectedCount={selectedCount}
          canEdit={canEdit}
          onRename={handleStartRename}
          onDuplicate={onDuplicate}
          onToggleFavorite={onToggleFavorite}
          onMove={onMove}
          onDelete={onDelete}
          onRestore={onRestore}
          onDeletePermanently={onDeletePermanently}
        />
      </ContextMenu>
    );
  }
);

ListTableRow.displayName = 'ListTableRow';
